/**
 * Procedures helpers shared between the chart-tab and the inline
 * editor. Same pattern as `conditions.ts` — keep helpers alongside
 * the type contracts they enforce, not exported from component files.
 */

import {
  EMPTY_BODY_SITE,
  type CodeDisplayName,
  type ConditionBodySite,
  type PatientProcedure,
} from '@/types/patient-chart-api';

/**
 * Lock procedure codes to the 5-field `CodeDisplayName` shape with
 * explicit-null fields. Master-data rows can ship 3-4 fields; JSON
 * stringify drops undefined keys → wire body has gaps. Normalize.
 *
 * **Defensive unwrap** (Andrew browser bug 2026-05-20): the
 * `/Master/ProcedureCodesList` endpoint occasionally ships rows as
 * `{ code: ActualCoding }` wrappers — same wrap pattern as
 * `/Master/DiagnosisCodes` that bit PR #225. When the input's `code`
 * field is itself an object, we lift the inner Coding's fields up
 * instead of copying the object verbatim (which would crash React
 * with "Objects are not valid as a React child" and ship a malformed
 * wire body to the backend). Idempotent on already-flat input.
 */
export function normalizeProcedureCode(
  c: CodeDisplayName | null | undefined,
): CodeDisplayName | null {
  if (!c) return null;
  // If `c.code` is itself an object, the wrapped shape — lift fields
  // from the inner Coding instead of the outer wrapper.
  const r = c as unknown as Record<string, unknown>;
  const inner =
    r.code && typeof r.code === 'object'
      ? (r.code as Record<string, unknown>)
      : r;
  return {
    code: typeof inner.code === 'string' ? inner.code : null,
    displayName:
      typeof inner.displayName === 'string'
        ? inner.displayName
        : typeof inner.display === 'string'
          ? inner.display
          : null,
    // Master ships PascalCase `System` sometimes (per the
    // DiagnosisCodes 400 precedent) — coalesce both spellings.
    system:
      typeof inner.system === 'string'
        ? inner.system
        : typeof inner.System === 'string'
          ? inner.System
          : null,
    description:
      typeof inner.description === 'string' ? inner.description : null,
    shortName:
      typeof inner.shortName === 'string' ? inner.shortName : null,
  };
}

/**
 * `bodySite` / `reason` Coding normalization for Procedures wire bodies.
 * Both fields ship the empty-Coding sentinel `{system:null, code:null,
 * display:null}` on Create when unset; UPDATE round-trips whatever the
 * existing record had (can be the sentinel OR a populated Coding).
 *
 * Coalesces missing fields against `EMPTY_BODY_SITE` so the wire body
 * always carries the three-key shape the backend expects.
 *
 * Returns a FRESH object each call (never the frozen sentinel) so
 * callers can safely mutate / spread the result.
 */
export function emptyProcedureCoding(
  bs: ConditionBodySite | null | undefined,
): ConditionBodySite {
  if (!bs) return { ...EMPTY_BODY_SITE };
  return {
    system: bs.system ?? null,
    code: bs.code ?? null,
    display: bs.display ?? null,
  };
}

/**
 * Read the existing record's `code` field and coerce to the wire shape
 * the picker emits. Used for the Edit pre-population path.
 */
export function readProcedureCode(
  c: PatientProcedure['code'] | null | undefined,
): CodeDisplayName | null {
  return normalizeProcedureCode(c);
}
